import React, { useState } from "react";
import { Button } from "@/dentlab/src/components/ui/button";
import { Input } from "@/dentlab/src/components/ui/input";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import { Label } from "@/dentlab/src/components/ui/label";
import {
	X,
	Eye,
	EyeOff,
	GripVertical,
	Plus,
	Minus,
	Settings2,
} from "lucide-react";
import {
	TabConfig,
	TabField,
	GeneralTabConfig,
} from "@/dentlab/src/types/tab-config.types";
import { normalizeGeneralTab } from "@/dentlab/src/config/density-config";
import { FieldSelector } from "./field-selector.component";
import {
	FIELD_REGISTRY,
	FIELD_CATEGORIES,
	getAvailableFieldsForTab,
} from "../field-registry";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/dentlab/src/components/ui/dropdown-menu";
import { ColorPicker } from "@/dentlab/src/components/ui/color-picker.component";
import { IconSelector } from "./icon-selector.component";
import { renderIcon } from "../utils/icon-mapper";
import { DragHandle } from "../../form-builder/components/ui/sortable-item";
import { cn } from "@/dentlab/src/lib/util";
import {
	getTextColorForBackground,
	hexToRgba,
} from "@/dentlab/src/components/utils/color.utils";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/dentlab/src/components/ui/popover";

interface TabCardEditorProps {
	tab: TabConfig;
	isSelected: boolean;
	onUpdate: (updates: Partial<TabConfig>) => void;
	onRemove: () => void;
	onSelect: () => void;
}

export const TabCardEditor: React.FC<TabCardEditorProps> = ({
	tab,
	isSelected,
	onUpdate,
	onRemove,
	onSelect,
}) => {
	const availableFields = getAvailableFieldsForTab(tab.type);
	const hasFields =
		tab.type !== "notes" &&
		tab.type !== "files" &&
		tab.type !== "job_history" &&
		tab.type !== "deliveries" &&
		tab.type !== "total";
	const usedFieldIds = hasFields
		? (tab as any).fields?.map((f: TabField) => f.fieldId) || []
		: [];

	const rawGeneralTab = hasFields ? (tab as GeneralTabConfig) : null;
	const generalTab = rawGeneralTab
		? normalizeGeneralTab(rawGeneralTab)
		: null;
	const columns = generalTab?.columns ?? 1;
	const rows = generalTab?.rows ?? generalTab?.fields?.length ?? 0;
	const fields: TabField[] = generalTab?.fields ?? [];

	const getFieldAt = (col: number, row: number): TabField | undefined =>
		fields.find((f) => (f.col ?? 0) === col && (f.row ?? 0) === row);

	const isCoveredBySpan = (col: number, row: number): boolean =>
		fields.some((f) => {
			const fc = f.col ?? 0;
			const fr = f.row ?? 0;
			const fs = f.colSpan ?? 1;
			return fr === row && fc < col && fc + fs > col;
		});

	const findFirstEmptyCell = (): { col: number; row: number } | null => {
		for (let r = 0; r < rows; r++) {
			for (let c = 0; c < columns; c++) {
				const occupied = fields.some((f) => {
					const fc = f.col ?? 0;
					const fr = f.row ?? 0;
					const fs = f.colSpan ?? 1;
					return fr === r && fc <= c && fc + fs > c;
				});
				if (!occupied) return { col: c, row: r };
			}
		}
		return null;
	};

	const addField = (fieldId: string) => {
		if (!hasFields) return;
		const cell = findFirstEmptyCell();
		const targetRow = cell ? cell.row : rows;
		const targetCol = cell ? cell.col : 0;
		const newField: TabField = {
			fieldId,
			required: false,
			col: targetCol,
			row: targetRow,
			colSpan: 1,
		};
		const newFields = [...fields, newField];
		const newRows = cell ? rows : rows + 1;
		onUpdate({ fields: newFields, rows: newRows } as any);
	};

	const addFieldToCell = (fieldId: string, col: number, row: number) => {
		if (!hasFields) return;
		const newField: TabField = {
			fieldId,
			required: false,
			col,
			row,
			colSpan: 1,
		};
		onUpdate({ fields: [...fields, newField] } as any);
	};

	const removeField = (fieldId: string) => {
		if (!hasFields) return;
		onUpdate({
			fields: fields.filter((f) => f.fieldId !== fieldId),
		} as any);
	};

	const updateField = (fieldId: string, updates: Partial<TabField>) => {
		if (!hasFields) return;
		onUpdate({
			fields: fields.map((f) =>
				f.fieldId === fieldId ? { ...f, ...updates } : f
			),
		} as any);
	};

	const setColumns = (newCols: number) => {
		if (!hasFields) return;
		// Redistribute fields into the new grid, filling left-to-right, top-to-bottom
		const sorted = [...fields].sort((a, b) => {
			const rowDiff = (a.row ?? 0) - (b.row ?? 0);
			if (rowDiff !== 0) return rowDiff;
			return (a.col ?? 0) - (b.col ?? 0);
		});
		const reflowed: TabField[] = [];
		let curRow = 0;
		let curCol = 0;
		for (const f of sorted) {
			const span = Math.min(f.colSpan ?? 1, newCols);
			if (curCol + span > newCols) {
				curRow++;
				curCol = 0;
			}
			reflowed.push({ ...f, col: curCol, row: curRow, colSpan: span });
			curCol += span;
			if (curCol >= newCols) {
				curRow++;
				curCol = 0;
			}
		}
		const newRows =
			reflowed.length > 0
				? Math.max(...reflowed.map((f) => f.row ?? 0)) + 1
				: 1;
		onUpdate({ columns: newCols, rows: newRows, fields: reflowed } as any);
	};

	const addRow = () => {
		if (!hasFields) return;
		onUpdate({ rows: rows + 1 } as any);
	};

	const removeRow = () => {
		if (!hasFields || rows <= 1) return;
		const lastRowHasFields = fields.some((f) => (f.row ?? 0) === rows - 1);
		if (lastRowHasFields) return;
		onUpdate({ rows: rows - 1 } as any);
	};

	const insertRowAfter = (afterRow: number) => {
		if (!hasFields) return;
		const shifted = fields.map((f) =>
			(f.row ?? 0) > afterRow ? { ...f, row: (f.row ?? 0) + 1 } : f
		);
		onUpdate({ rows: rows + 1, fields: shifted } as any);
	};

	const insertColumnAfter = (afterCol: number) => {
		if (!hasFields || columns >= 3) return;
		const newCols = columns + 1;
		const shifted = fields.map((f) => {
			const fc = f.col ?? 0;
			if (fc > afterCol) return { ...f, col: fc + 1 };
			if (fc === afterCol && (f.colSpan ?? 1) > 1) {
				return { ...f, colSpan: (f.colSpan ?? 1) + 1 };
			}
			return f;
		});
		const maxRow = Math.max(rows, ...shifted.map((f) => (f.row ?? 0) + 1));
		onUpdate({ columns: newCols, rows: maxRow, fields: shifted } as any);
	};

	const cycleColSpan = (fieldId: string) => {
		if (!hasFields || columns <= 1) return;
		const field = fields.find((f) => f.fieldId === fieldId);
		if (!field) return;
		const fc = field.col ?? 0;
		const currentSpan = field.colSpan ?? 1;
		const maxSpan = columns - fc;
		const nextSpan = currentSpan >= maxSpan ? 1 : currentSpan + 1;
		updateField(fieldId, { colSpan: nextSpan });
	};

	const swapFields = (fieldId1: string, fieldId2: string) => {
		if (!hasFields) return;
		const f1 = fields.find((f) => f.fieldId === fieldId1);
		const f2 = fields.find((f) => f.fieldId === fieldId2);
		if (!f1 || !f2) return;
		onUpdate({
			fields: fields.map((f) => {
				if (f.fieldId === fieldId1)
					return {
						...f,
						col: f2.col,
						row: f2.row,
						colSpan: f2.colSpan,
					};
				if (f.fieldId === fieldId2)
					return {
						...f,
						col: f1.col,
						row: f1.row,
						colSpan: f1.colSpan,
					};
				return f;
			}),
		} as any);
	};

	const moveFieldToCell = (fieldId: string, col: number, row: number) => {
		if (!hasFields) return;
		const existing = getFieldAt(col, row);
		if (existing && existing.fieldId !== fieldId) {
			swapFields(fieldId, existing.fieldId);
		} else {
			updateField(fieldId, { col, row });
		}
	};

	const getTabLabel = (): string => {
		switch (tab.type) {
			case "general":
				return (tab as GeneralTabConfig).label || "Allgemein";
			case "notes":
				return "Notizen";
			case "files":
				return "Dateien";
			case "job_history":
				return "Auftragshistorie";
			case "deliveries":
				return "Lieferungen";
			case "total":
				return "Total";
			default:
				return "Tab";
		}
	};

	const getTabTypeDescription = () => {
		switch (tab.type) {
			case "general":
				return "Frei konfigurierbarer Tab mit individuellen Feldern und Bezeichnung.";
			case "notes":
				return "Vordefinierter Tab für Notizen. Die Funktionalität ist fest vorgegeben und kann nicht konfiguriert werden.";
			case "files":
				return "Vordefinierter Tab für Dateiverwaltung. Die Funktionalität ist fest vorgegeben und kann nicht konfiguriert werden.";
			case "job_history":
				return "Vordefinierter Tab für die Auftragshistorie. Die Funktionalität ist fest vorgegeben und kann nicht konfiguriert werden.";
			case "deliveries":
				return "Vordefinierter Tab für Lieferungen. Die Funktionalität ist fest vorgegeben und kann nicht konfiguriert werden.";
			case "total":
				return "Zeigt die Totalübersicht des aktuell ausgewählten Dokuments.";
			default:
				return "";
		}
	};

	const textColorClass = getTextColorForBackground(tab.color);
	const headerBgColor = tab.color || "#F9FAFB";
	const contentBgColor =
		hexToRgba(tab.color, 0.1) || "rgba(249, 250, 251, 0.5)";

	// Determine width based on tab type and column count
	const isSimpleTab =
		tab.type === "notes" ||
		tab.type === "files" ||
		tab.type === "job_history" ||
		tab.type === "deliveries" ||
		tab.type === "total";
	const tabWidthPx = isSimpleTab
		? 192
		: columns <= 1
			? 384
			: columns === 2
				? 520
				: 660;

	return (
		<div
			className={cn(
				"flex-shrink-0 border rounded-lg transition-all",
				isSelected
					? "border-blue-500"
					: "border-gray-200 hover:border-gray-300"
			)}
			style={{ width: `${tabWidthPx}px` }}
			onClick={onSelect}
		>
			{/* Tab Header */}
			<div
				className="p-4 border-b rounded-t-lg"
				style={{ backgroundColor: headerBgColor }}
			>
				<div className="flex flex-col gap-2">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<DragHandle>
								<GripVertical
									className={cn(
										"h-4 w-4 drag-handle cursor-move",
										textColorClass
									)}
								/>
							</DragHandle>
							{tab.icon &&
								renderIcon(tab.icon, {
									className: cn("h-4 w-4", textColorClass),
								})}
							<span
								className={cn(
									"font-medium text-sm",
									textColorClass
								)}
							>
								{getTabLabel()}
							</span>
						</div>
						{tab.type !== "total" && (
							<TooltipWrapper title="Tab entfernen">
								<Button
									size="sm"
									variant="ghost"
									className={cn(
										"h-6 w-6 p-0 hover:bg-black/10",
										textColorClass
									)}
									onClick={(e) => {
										e.stopPropagation();
										onRemove();
									}}
									aria-label="Tab entfernen"
								>
									<X className="h-3 w-3" />
								</Button>
							</TooltipWrapper>
						)}
					</div>
					<p
						className={cn(
							"text-sm",
							textColorClass,
							textColorClass === "text-white"
								? "opacity-90"
								: "opacity-75"
						)}
					>
						{getTabTypeDescription()}
					</p>
				</div>
			</div>

			{/* Tab Content */}
			<div
				className="p-4 space-y-4 rounded-b-lg"
				style={{ backgroundColor: contentBgColor }}
			>
				{/* Anzeige Section */}
				<div>
					<h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
						Anzeige
					</h4>
					<div className="flex items-center gap-2 flex-wrap">
						{/* Visibility toggle */}
						<TooltipWrapper
							title={
								tab.isDefaultActive
									? "standardmässig offen"
									: "standardmässig ausgeblendet"
							}
						>
							<Button
								variant="outline"
								size="sm"
								onClick={(e) => {
									e.stopPropagation();
									onUpdate({
										isDefaultActive: !tab.isDefaultActive,
									});
								}}
								className="h-8 w-8 p-0"
								aria-label={
									tab.isDefaultActive
										? "standardmässig offen"
										: "standardmässig ausgeblendet"
								}
							>
								{tab.isDefaultActive ? (
									<Eye className="h-4 w-4" />
								) : (
									<EyeOff className="h-4 w-4 text-gray-400" />
								)}
							</Button>
						</TooltipWrapper>

						{/* Color picker */}
						<TooltipWrapper title="Tab-Farbe wählen">
							<div onClick={(e) => e.stopPropagation()}>
								<ColorPicker
									currentColor={tab.color}
									onColorSelect={(color) =>
										onUpdate({ color: color || undefined })
									}
									showReset={true}
									label="Tab-Farbe wählen"
								/>
							</div>
						</TooltipWrapper>

						{/* Icon selector */}
						<TooltipWrapper title="Tab-Icon wählen">
							<div onClick={(e) => e.stopPropagation()}>
								<IconSelector
									currentIcon={tab.icon}
									onIconSelect={(icon) => onUpdate({ icon })}
								/>
							</div>
						</TooltipWrapper>

						{/* Title input (only for general tabs) */}
						{tab.type === "general" && (
							<Input
								value={(tab as GeneralTabConfig).label}
								onChange={(e) =>
									onUpdate({ label: e.target.value })
								}
								onClick={(e) => e.stopPropagation()}
								placeholder="Tab-Titel"
								className="flex-1 h-8 min-w-[120px]"
							/>
						)}
					</div>
				</div>

				{/* Fields Section — Grid Editor */}
				{hasFields && (
					<div onClick={(e) => e.stopPropagation()}>
						<h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
							Felder
						</h4>

						{/* Column/Row controls */}
						<div className="flex items-center gap-4 mb-3">
							<div className="flex items-center gap-1.5">
								<span className="text-xs text-gray-500">
									Spalten:
								</span>
								{[1, 2, 3].map((n) => (
									<Button
										key={n}
										size="sm"
										variant={
											columns === n
												? "default"
												: "outline"
										}
										className="h-6 w-6 p-0 text-xs"
										onClick={() => setColumns(n)}
										aria-label={`${n} Spalte${
											n > 1 ? "n" : ""
										}`}
									>
										{n}
									</Button>
								))}
								{columns < 3 && (
									<Button
										size="sm"
										variant="ghost"
										className="h-6 px-1 text-[10px] text-gray-400"
										onClick={() =>
											insertColumnAfter(columns - 1)
										}
										aria-label="Spalte einfügen"
									>
										+
									</Button>
								)}
							</div>
							<div className="flex items-center gap-1.5">
								<span className="text-xs text-gray-500">
									Zeilen:
								</span>
								<Button
									size="sm"
									variant="outline"
									className="h-6 w-6 p-0"
									onClick={addRow}
									aria-label="Zeile hinzufügen"
								>
									<Plus className="h-3 w-3" />
								</Button>
								<span className="text-xs font-medium">
									{rows}
								</span>
								<Button
									size="sm"
									variant="outline"
									className="h-6 w-6 p-0"
									onClick={removeRow}
									disabled={
										rows <= 1 ||
										fields.some(
											(f) => (f.row ?? 0) === rows - 1
										)
									}
									aria-label="Zeile entfernen"
								>
									<Minus className="h-3 w-3" />
								</Button>
							</div>
						</div>

						{/* Visual Grid with row insert dividers */}
						<div className="mb-3">
							{Array.from({ length: Math.max(rows, 1) }).map(
								(_, rowIdx) => {
									return (
										<React.Fragment key={`row-${rowIdx}`}>
											{rowIdx > 0 && (
												<div
													className="group h-2 flex items-center cursor-pointer"
													onClick={() =>
														insertRowAfter(
															rowIdx - 1
														)
													}
													aria-label={`Zeile einfügen`}
												>
													<div className="flex-1 h-px bg-transparent group-hover:bg-blue-300 transition-colors" />
													<Plus className="h-3 w-3 text-transparent group-hover:text-blue-400 transition-colors shrink-0" />
													<div className="flex-1 h-px bg-transparent group-hover:bg-blue-300 transition-colors" />
												</div>
											)}
											<div
												className="grid gap-1"
												style={{
													gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
												}}
											>
												{Array.from({
													length: columns,
												}).map((_, colIdx) => {
													if (
														isCoveredBySpan(
															colIdx,
															rowIdx
														)
													)
														return null;

													const field = getFieldAt(
														colIdx,
														rowIdx
													);

													if (field) {
														const span =
															field.colSpan ?? 1;
														if (
															(field.col ?? 0) !==
															colIdx
														)
															return null;
														const fieldDef =
															FIELD_REGISTRY[
																field.fieldId
															];
														return (
															<div
																key={`${rowIdx}-${colIdx}`}
																className="border border-gray-300 rounded p-1.5 bg-white text-xs flex items-center gap-1 min-h-[32px] min-w-0 overflow-hidden cursor-move"
																style={{
																	gridColumn: `${
																		colIdx +
																		1
																	} / span ${span}`,
																}}
																draggable
																onDragStart={(
																	e
																) => {
																	e.dataTransfer.setData(
																		"fieldId",
																		field.fieldId
																	);
																}}
																onDragOver={(
																	e
																) =>
																	e.preventDefault()
																}
																onDrop={(e) => {
																	e.preventDefault();
																	const draggedId =
																		e.dataTransfer.getData(
																			"fieldId"
																		);
																	if (
																		draggedId &&
																		draggedId !==
																			field.fieldId
																	) {
																		swapFields(
																			draggedId,
																			field.fieldId
																		);
																	}
																}}
															>
																<span className="flex-1 truncate font-medium">
																	{fieldDef?.label ||
																		field.fieldId}
																</span>
																<div className="flex items-center gap-0.5 shrink-0">
																	{field.required && (
																		<span className="text-red-500 text-[10px]">
																			*
																		</span>
																	)}
																	{columns >
																		1 && (
																		<TooltipWrapper
																			title={`Breite: ${span}/${columns}`}
																		>
																			<Button
																				size="sm"
																				variant="ghost"
																				className="h-5 w-5 p-0 text-[10px]"
																				onClick={(
																					e
																				) => {
																					e.stopPropagation();
																					cycleColSpan(
																						field.fieldId
																					);
																				}}
																				aria-label="Spaltenbreite ändern"
																			>
																				{
																					span
																				}
																			</Button>
																		</TooltipWrapper>
																	)}
																	<TooltipWrapper title="Feldeinstellungen">
																		<FieldSettingsPopover
																			field={
																				field
																			}
																			onUpdate={(
																				updates
																			) =>
																				updateField(
																					field.fieldId,
																					updates
																				)
																			}
																			onRemove={() =>
																				removeField(
																					field.fieldId
																				)
																			}
																		/>
																	</TooltipWrapper>
																</div>
															</div>
														);
													}

													return (
														<EmptyCellWithSelector
															key={`${rowIdx}-${colIdx}`}
															availableFields={availableFields.filter(
																(f: string) =>
																	!usedFieldIds.includes(
																		f
																	)
															)}
															onSelect={(
																fieldId
															) =>
																addFieldToCell(
																	fieldId,
																	colIdx,
																	rowIdx
																)
															}
															colIdx={colIdx}
															rowIdx={rowIdx}
															onDrop={(
																draggedId
															) =>
																moveFieldToCell(
																	draggedId,
																	colIdx,
																	rowIdx
																)
															}
														/>
													);
												})}
											</div>
										</React.Fragment>
									);
								}
							)}
						</div>

						{/* Add field selector */}
						<FieldSelector
							availableFields={availableFields.filter(
								(f: string) => !usedFieldIds.includes(f)
							)}
							onSelect={addField}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

const FieldSettingsPopover: React.FC<{
	field: TabField;
	onUpdate: (updates: Partial<TabField>) => void;
	onRemove: () => void;
}> = ({ field, onUpdate, onRemove }) => {
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					size="sm"
					variant="ghost"
					className="h-5 w-5 p-0"
					aria-label="Feldeinstellungen"
				>
					<Settings2 className="h-3 w-3" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-48 p-2" align="end">
				<div className="space-y-2">
					<div className="flex items-center gap-2">
						<Checkbox
							id={`req-${field.fieldId}`}
							checked={field.required}
							onCheckedChange={(checked) =>
								onUpdate({ required: !!checked })
							}
						/>
						<Label
							htmlFor={`req-${field.fieldId}`}
							className="text-xs cursor-pointer"
						>
							Pflichtfeld
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Checkbox
							id={`title-${field.fieldId}`}
							checked={field.useInTitle ?? false}
							onCheckedChange={(checked) =>
								onUpdate({ useInTitle: !!checked })
							}
						/>
						<Label
							htmlFor={`title-${field.fieldId}`}
							className="text-xs cursor-pointer"
						>
							Im Tab-Titel anzeigen
						</Label>
					</div>
					<Button
						size="sm"
						variant="ghost"
						className="w-full h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 justify-start"
						onClick={onRemove}
					>
						<X className="h-3 w-3 mr-1" />
						Feld entfernen
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
};

const EmptyCellWithSelector: React.FC<{
	availableFields: string[];
	onSelect: (fieldId: string) => void;
	colIdx: number;
	rowIdx: number;
	onDrop: (draggedId: string) => void;
}> = ({ availableFields, onSelect, colIdx, rowIdx, onDrop }) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const isDraggingOver = React.useRef(false);

	return (
		<DropdownMenu
			open={menuOpen}
			onOpenChange={(open) => {
				if (open && isDraggingOver.current) return;
				setMenuOpen(open);
			}}
		>
			<DropdownMenuTrigger asChild>
				<div
					className="border border-dashed border-gray-300 rounded min-h-[32px] flex items-center justify-center text-gray-400 hover:border-gray-400 hover:bg-gray-50 transition-colors cursor-pointer"
					onDragEnter={() => {
						isDraggingOver.current = true;
					}}
					onDragOver={(e) => {
						e.preventDefault();
						e.currentTarget.classList.add(
							"bg-blue-50",
							"border-blue-300"
						);
					}}
					onDragLeave={(e) => {
						isDraggingOver.current = false;
						e.currentTarget.classList.remove(
							"bg-blue-50",
							"border-blue-300"
						);
					}}
					onDrop={(e) => {
						e.preventDefault();
						e.stopPropagation();
						isDraggingOver.current = false;
						e.currentTarget.classList.remove(
							"bg-blue-50",
							"border-blue-300"
						);
						const draggedId = e.dataTransfer.getData("fieldId");
						if (draggedId) onDrop(draggedId);
					}}
				>
					<Plus className="h-3 w-3" />
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className="w-64 max-h-80 overflow-y-auto"
				align="start"
			>
				{availableFields.includes("patient_select") && (
					<DropdownMenuItem
						onClick={() => onSelect("patient_select")}
						className="cursor-pointer"
					>
						Patientenauswahl
					</DropdownMenuItem>
				)}
				{availableFields.includes("practitioner_select") && (
					<DropdownMenuItem
						onClick={() => onSelect("practitioner_select")}
						className="cursor-pointer"
					>
						Behandlerauswahl
					</DropdownMenuItem>
				)}
				{Object.entries(FIELD_CATEGORIES).map(
					([category, fieldIds]) => {
						const available = fieldIds.filter((id) =>
							availableFields.includes(id)
						);
						if (available.length === 0) return null;
						return (
							<DropdownMenuSub key={category}>
								<DropdownMenuSubTrigger>
									{category === "Patient"
										? "Patienteninformationen"
										: category === "Behandler"
											? "Behandlerinformationen"
											: category}
								</DropdownMenuSubTrigger>
								<DropdownMenuPortal>
									<DropdownMenuSubContent>
										{available.map((fieldId) => (
											<DropdownMenuItem
												key={fieldId}
												onClick={() =>
													onSelect(fieldId)
												}
												className="cursor-pointer"
											>
												{FIELD_REGISTRY[fieldId]
													?.label || fieldId}
											</DropdownMenuItem>
										))}
									</DropdownMenuSubContent>
								</DropdownMenuPortal>
							</DropdownMenuSub>
						);
					}
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
